import React, { useMemo, useState } from 'react';
import { useMutation } from '../../hooks/useMutation';
import './PasswordPanel.css';

function PasswordPanel({
  canManageUsers,
  onChangePassword,
  onClose,
  onResetPassword,
  users = [],
  user
}) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetUserId, setResetUserId] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [validationError, setValidationError] = useState('');

  const managedUsers = useMemo(
    () => users.filter(entry => entry.id !== user?.id),
    [user?.id, users]
  );

  const {
    mutate: runChangePassword,
    isLoading: isChangingPassword,
    error: changeError,
    successMessage: changeSuccess
  } = useMutation(onChangePassword, {
    successMessage: 'Password updated successfully.',
    errorFallback: 'Unable to update password.',
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  });

  const {
    mutate: runResetPassword,
    isLoading: isResettingPassword,
    error: resetError,
    successMessage: resetSuccess
  } = useMutation(onResetPassword, {
    successMessage: 'Password reset. The user will be prompted to change it after login.',
    errorFallback: 'Unable to reset password.',
    onSuccess: () => setResetPassword('')
  });

  const isSubmitting = isChangingPassword || isResettingPassword;
  const statusMessage = changeSuccess || resetSuccess;
  const errorMessage = validationError || changeError || resetError;

  const handleChangePassword = async (event) => {
    event.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      setValidationError('Complete all password fields to continue.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setValidationError('New password and confirmation do not match.');
      return;
    }

    setValidationError('');
    await runChangePassword({ currentPassword, newPassword });
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();

    if (!resetUserId || !resetPassword) {
      setValidationError('Choose a user and provide a temporary password.');
      return;
    }

    setValidationError('');
    await runResetPassword({ newPassword: resetPassword, userId: resetUserId });
  };

  return (
    <div className="password-panel-backdrop" role="dialog" aria-modal="true" aria-labelledby="password-panel-title">
      <div className="password-panel">
        <div className="password-panel-header">
          <div>
            <span className="password-panel-eyebrow">Security</span>
            <h2 id="password-panel-title">Password Management</h2>
          </div>
          <button type="button" className="password-panel-close" onClick={onClose}>Close</button>
        </div>

        <p className="password-panel-copy">
          Change your password here. If someone forgets theirs, the director can reset it and require a new password on next sign-in.
        </p>

        {(statusMessage || errorMessage) && (
          <div className={`password-panel-message ${errorMessage ? 'error' : 'success'}`}>
            {errorMessage || statusMessage}
          </div>
        )}

        {user?.mustChangePassword && (
          <div className="password-panel-message warning">
            Your password was reset. Change it before continuing normal use.
          </div>
        )}

        <form className="password-panel-form" onSubmit={handleChangePassword}>
          <h3>Change My Password</h3>
          <label htmlFor="current-password">Current Password</label>
          <input
            id="current-password"
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            autoComplete="current-password"
          />

          <label htmlFor="new-password">New Password</label>
          <input
            id="new-password"
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            autoComplete="new-password"
          />

          <label htmlFor="confirm-password">Confirm New Password</label>
          <input
            id="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            autoComplete="new-password"
          />

          <button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Update Password'}
          </button>
        </form>

        {canManageUsers && (
          <form className="password-panel-form secondary" onSubmit={handleResetPassword}>
            <h3>Workspace Password Reset</h3>
            <label htmlFor="reset-user">User</label>
            <select
              id="reset-user"
              value={resetUserId}
              onChange={(event) => setResetUserId(event.target.value)}
            >
              <option value="">Select a user</option>
              {managedUsers.map(entry => (
                <option key={entry.id} value={entry.id}>
                  {entry.email}
                </option>
              ))}
            </select>

            <label htmlFor="reset-password">Temporary Password</label>
            <input
              id="reset-password"
              type="password"
              value={resetPassword}
              onChange={(event) => setResetPassword(event.target.value)}
              autoComplete="new-password"
            />

            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Resetting…' : 'Reset User Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default PasswordPanel;
